<script setup lang="ts">
import type { GraphqlDatatableColumn } from '~/components/GqlDatatable.vue';
import dayjs from 'dayjs';
import { computed, ref } from 'vue';
import { CompromissoForm } from '~/lib/fw';
import { useAuthenticationStore } from '~/stores/authenticationStore';

interface Props {
  pastaCodigos?: string[];
  compromisso?: any;
  fluxo?: any;
}

const props = defineProps<Props>();

const emit = defineEmits(['close']);

const mostrarHistoricoSolicitacoes = ref<string>('h');
const colunas: GraphqlDatatableColumn[] = [
  {
    key: 'compromisso',
    title: 'COMPROMISSO',
    width: 120,
  },
  {
    key: 'responsavel',
    title: 'RESPONSÁVEL',
    width: 120,
  },
  {
    dataIndex: 'dataInicioText',
    key: 'dataInicio',
    sortBy: ['dataInicio'],
    sorter: true,
    title: 'SOLICITADO EM',
    width: 120,
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'dataVencimentoText',
    key: 'dataVencimento',
    sortBy: ['dataVencimento'],
    sorter: true,
    title: 'VENCIMENTO EM',
    width: 120,
  },
  {
    dataIndex: 'dataConclusaoText',
    key: 'dataConclusao',
    sortBy: ['dataConclusao'],
    sorter: true,
    title: 'CONCLUÍDO EM',
    width: 120,
  },
];
const planilhaRef = ref<any>(null);

function disabledDate(current: any) {
  return (
    current.day() === 0 ||
    current.day() === 6 ||
    current <= dayjs().subtract(1, 'day').endOf('day')
  );
}

const authenticationStore = useAuthenticationStore();
const buttonsDisabled = computed(() => {
  if (!props.compromisso) {
    return false;
  }
  return authenticationStore.user!.id !== props.compromisso.responsavelCodigo;
});

async function preSubmit(model: any) {
  if (model.tipo.value === 1 || model.tipo.value === 2) {
    await planilhaRef.value.upload();
  }
  return model;
}
</script>

<template>
  <CompromissoForm
    action="planilha-atualizacao-debito/enviar-planilha-para-atualizacao"
    :pasta-codigos="props.pastaCodigos ?? []"
    :compromisso-codigos="props.compromisso ? [props.compromisso.codigo] : []"
    :wait-for-result="true"
    :show-upload-files="false"
    :submit-button-disabled="buttonsDisabled"
    :pre-submit="preSubmit"
    @close="() => emit('close')"
  >
    <template #form="{ model }">
      <a-collapse
        v-if="props.pastaCodigos?.length === 1"
        v-model:active-key="mostrarHistoricoSolicitacoes"
        style="margin-bottom: 24px"
      >
        <a-collapse-panel key="h" header="HISTÓRICO DE SOLICITAÇÕES">
          <CompromissoGqlDatatable
            :columns="colunas"
            :show-dates-filter="false"
            :show-reload-button="false"
            :show-status-search-bar="false"
            :show-filter="false"
            :variables="{
              order: [{ dataInicio: 'DESC' }],
              where: {
                tipo: {
                  eq: 50,
                },
                fluxo: {
                  pastaCodigo: {
                    in: [props.pastaCodigos[0]],
                  },
                },
              },
            }"
          />
        </a-collapse-panel>
      </a-collapse>

      <a-alert show-icon style="margin-top: 16px">
        <template #message>
          <a href="/TEMPLATE_PLANILHA_ACORDO_DESCUMPRIDO.xlsx"
            >Clique aqui para baixar o EXCEL padrão para acordo descumprido</a
          >
        </template>
      </a-alert>

      <a-alert show-icon style="margin-top: 8px; margin-bottom: 24px">
        <template #message>
          <a href="/TEMPLATE_PLANILHA_ATUALIZACAO_DO_VALOR_DA_CAUSA.xlsx"
            >Clique aqui para baixar o EXCEL padrão para atualização do valor da
            causa</a
          >
        </template>
      </a-alert>

      <a-form-item
        label="Tipo"
        name="tipo"
        :rules="[
          {
            message: 'Tipo é obrigatório',
            required: true,
            trigger: 'change',
            type: 'number',
          },
        ]"
      >
        <a-radio-group v-model:value="model.tipo" button-style="solid">
          <a-radio-button :value="1"> Contrato descumprido </a-radio-button>
          <a-radio-button :value="2">
            Atualização do valor da causa
          </a-radio-button>
          <a-radio-button :value="3">
            Atualização realizada pelo escritório
          </a-radio-button>
        </a-radio-group>
      </a-form-item>

      <a-form-item
        v-if="model.tipo === 1 || model.tipo === 2"
        label="Planilha"
        name="planilha"
        :rules="{
          message: 'Planilha é obrigatória',
          required: true,
          trigger: 'change',
          type: 'object',
        }"
      >
        <UploadFile ref="planilhaRef" v-model:file="model.planilha" />
      </a-form-item>

      <a-form-item
        label="Prazo"
        name="dataPrazo"
        :rules="[{ message: 'Prazo é obrigatório', required: true }]"
      >
        <a-date-picker
          v-model:value="model.dataPrazo"
          format="DD/MM/YYYY"
          value-format="DD/MM/YYYY"
          placeholder="Selecione uma data"
          style="width: 100%"
          :disabled-date="disabledDate"
        />
      </a-form-item>

      <a-form-item label="Observação" name="observacao">
        <a-textarea v-model:value="model.observacao" :rows="4" />
      </a-form-item>
    </template>
  </CompromissoForm>
</template>
